import type { ClientMessage, ServerMessage } from "@golazo/shared";
import type { HallOfFameEntry } from "../career/career.js";
import { hasMore, mergeHall, packSave, parseSave, pickCareer, type SaveState } from "../career/cloud.js";
import { loadHallOfFame, onHallChange, replaceHallOfFame } from "../career/hallOfFame.js";
import type { Career } from "../career/model.js";
import { careerSavedAt, loadCareer, onCareerChange, saveCareer } from "../career/store.js";
import { unlockStore, type KitUnlocks } from "../career/unlocks.js";

/**
 * La carrera offline en la cuenta: se junta al conectar y se sube en cada paso.
 *
 * Igual que `profileSync`: el servidor guarda texto y no decide nada; las
 * reglas de quien gana viven en `career/cloud.ts`, que es puro y tiene tests.
 *
 * ## Cuando NO se sube
 *
 * Antes del primer `welcome` de cada conexion. Subir la copia de este aparato
 * sin haber visto la de la cuenta podria pisar una carrera mas avanzada jugada
 * en otro sitio. Primero se junta, y despues se sube lo que haya quedado.
 */
export interface CareerCloud {
  /** Mensaje del servidor. Devuelve true si ha cambiado algo de aqui con lo que llego. */
  handle(msg: ServerMessage): boolean;
  dispose(): void;
}

/** Lo que el modulo necesita de lo guardado en este aparato. Inyectable para los tests. */
export interface LocalCareerData {
  read(): SaveState;
  adoptCareer(career: Career, at: number): void;
  mergeUnlocks(unlocks: KitUnlocks): void;
  replaceHall(hall: HallOfFameEntry[]): void;
  /** Cambios hechos AQUI (jugar, fichar, retirarse, ganar una camiseta). */
  subscribe(fn: () => void): () => void;
}

export interface CareerCloudOptions {
  send(msg: ClientMessage): void;
  onState?(fn: (state: string) => void): () => void;
  local?: LocalCareerData;
  /** Espera antes de subir: se juntan los guardados seguidos de un mismo paso. */
  debounceMs?: number;
  /** Aviso de que la carrera de aqui la ha cambiado una copia de la cuenta. */
  onAdopted?(): void;
}

/** Lo guardado en este navegador, tal cual lo usan la carrera y Personalizar. */
export function browserCareerData(): LocalCareerData {
  // Mientras se aplica lo que llega de la cuenta, los avisos de cambio no son
  // "cambios de aqui": no hay que volver a subirlos.
  let applying = false;
  return {
    read: () => ({
      career: loadCareer(),
      at: careerSavedAt(),
      unlocks: unlockStore().get(),
      hall: loadHallOfFame(),
    }),
    adoptCareer(career, at) {
      applying = true;
      try {
        saveCareer(career, undefined, { at, source: "cloud" });
      } finally {
        applying = false;
      }
    },
    mergeUnlocks(unlocks) {
      applying = true;
      try {
        unlockStore().merge(unlocks);
      } finally {
        applying = false;
      }
    },
    replaceHall(hall) {
      replaceHallOfFame(hall);
    },
    subscribe(fn) {
      const offCareer = onCareerChange((source) => {
        if (source === "local") fn();
      });
      const offUnlocks = unlockStore().subscribe(() => {
        if (!applying) fn();
      });
      const offHall = onHallChange((source) => {
        if (source === "local") fn();
      });
      return () => {
        offCareer();
        offUnlocks();
        offHall();
      };
    },
  };
}

export function createCareerCloud(opts: CareerCloudOptions): CareerCloud {
  const local = opts.local ?? browserCareerData();
  const debounceMs = opts.debounceMs ?? 1200;
  /** Ya se ha juntado con la copia de la cuenta en esta conexion. */
  let ready = false;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Lo ultimo que se subio, para no mandar dos veces lo mismo. */
  let lastSent: string | null = null;

  const pushNow = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!ready || disposed) return;
    const text = packSave(local.read());
    if (!text || text === lastSent) return;
    lastSent = text;
    opts.send({ type: "career:push", save: text });
  };

  const schedule = (): void => {
    if (!ready || disposed) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(pushNow, debounceMs);
  };

  const unsubscribe = local.subscribe(schedule);
  const unsubState = opts.onState?.((state) => {
    // Al caerse la conexion, lo de la cuenta puede haber cambiado desde otro
    // sitio: no se vuelve a subir nada hasta haberlo visto otra vez.
    if (state !== "online") {
      ready = false;
      lastSent = null;
    }
  });

  /** Junta lo de aqui con lo de la cuenta. `force`: el jugador ha pedido ser esa cuenta. */
  const merge = (raw: string | null | undefined, force: boolean): boolean => {
    const mine = local.read();
    const theirs = parseSave(raw);
    let changed = false;
    if (theirs) {
      const newKits = theirs.unlocks.kits.some((k) => !mine.unlocks.kits.includes(k));
      if (newKits || (theirs.unlocks.custom && !mine.unlocks.custom)) {
        local.mergeUnlocks(theirs.unlocks);
        changed = true;
      }
      const hall = mergeHall(mine.hall, theirs.hall);
      if (hall.length > mine.hall.length) {
        local.replaceHall(hall);
        changed = true;
      }
    }
    /*
     * Canje a proposito (cambiarse a otra cuenta): su carrera manda aunque vaya
     * por detras. Pero si es la MISMA carrera (enlazar Google tambien llega
     * como `account:redeemed`, sin cambiar de cuenta), gana la mas avanzada:
     * forzar ahi podria pisar un partido recien jugado que aun no se subio.
     */
    const forced = force && theirs?.career && (!mine.career || theirs.career.seed !== mine.career.seed);
    const pick = theirs ? (forced ? "remote" : pickCareer(mine, theirs)) : "local";
    if (pick === "remote" && theirs?.career) {
      local.adoptCareer(theirs.career, theirs.at);
      changed = true;
      opts.onAdopted?.();
    }
    ready = true;
    if (hasMore(mine, theirs, pick)) pushNow();
    return changed;
  };

  return {
    handle(msg) {
      if (disposed) return false;
      if (msg.type === "welcome") return merge(msg.career, false);
      if (msg.type === "account:redeemed") return merge(msg.career, true);
      return false;
    },
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
      unsubscribe();
      unsubState?.();
    },
  };
}
